import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useStages } from '../../redux/hooks/useStages';
import { Stage } from '../../types/types';
import { getNextStage } from '../../utils/stageUtils';

interface AdvanceStageButtonProps {
    candidateId: string;
    currentStageId: string;
    onStageChange: (candidateId: string, newStageId: string, notes?: string) => void;
}

const AdvanceStageButton: React.FC<AdvanceStageButtonProps> = ({
    candidateId,
    currentStageId,
    onStageChange
}) => {
    // Redux hooks
    const { activeStagesSorted: allStages, loading: stagesLoading, ensureDataLoaded } = useStages();

    // Local state
    const [availableStages, setAvailableStages] = useState<Stage[]>([]);
    const [defaultStageId, setDefaultStageId] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedStageId, setSelectedStageId] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    // Load stages on mount
    useEffect(() => {
        ensureDataLoaded();
    }, [ensureDataLoaded]);

    // Filter available stages when stages or currentStageId changes
    useEffect(() => {
        if (allStages.length > 0) {
            // Filter out current stage and sort by display order
            const filteredStages = allStages
                .filter(stage => stage.id !== currentStageId && stage.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder);

            setAvailableStages(filteredStages);

            // Default to the next stage, falling back to the first available
            const defaultId = getNextStage(allStages, currentStageId)?.id ?? filteredStages[0]?.id ?? '';
            setDefaultStageId(defaultId);
            setSelectedStageId(defaultId);
        }
    }, [allStages, currentStageId]);

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setNotes('');
        setSelectedStageId(defaultStageId);
    };

    const handleStageChange = async () => {
        if (!selectedStageId) return;

        setLoading(true);
        try {
            // Use the callback provided by parent component
            await onStageChange(candidateId, selectedStageId, notes.trim() || undefined);
            handleCloseModal();
        } catch (error) {
            console.error('Error changing stage:', error);
            alert('Failed to change candidate stage. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = availableStages.length === 0 || stagesLoading;

    return (
        <>
            <Button
                variant="success"
                onClick={handleOpenModal}
                disabled={isDisabled}
            >
                {stagesLoading ? (
                    <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                    </>
                ) : (
                    <>
                        <i className="bi bi-arrow-right-circle me-1"></i> Change Stage
                    </>
                )}
            </Button>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Candidate Stage</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {stagesLoading ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" />
                            <p className="mt-2 text-muted">Loading stages...</p>
                        </div>
                    ) : availableStages.length === 0 ? (
                        <div className="text-center py-3">
                            <i className="bi bi-info-circle display-4 text-muted"></i>
                            <p className="text-muted mt-2">No other stages available to move to.</p>
                        </div>
                    ) : (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Select New Stage</Form.Label>
                                <Form.Select
                                    value={selectedStageId}
                                    onChange={(e) => setSelectedStageId(e.target.value)}
                                    disabled={loading}
                                >
                                    {availableStages.map(stage => (
                                        <option key={stage.id} value={stage.id}>
                                            {stage.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {availableStages.find(s => s.id === selectedStageId)?.description}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes (Optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add notes about this stage change"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    disabled={loading}
                                />
                                <Form.Text className="text-muted">
                                    These notes will be added to the candidate's activity history.
                                </Form.Text>
                            </Form.Group>

                            {/* Show selected stage info */}
                            {selectedStageId && (
                                <div className="bg-light p-3 rounded">
                                    <h6 className="mb-2">
                                        <i className="bi bi-arrow-right me-2"></i>
                                        Moving to: {availableStages.find(s => s.id === selectedStageId)?.name}
                                    </h6>
                                    {availableStages.find(s => s.id === selectedStageId)?.description && (
                                        <p className="text-muted small mb-0">
                                            {availableStages.find(s => s.id === selectedStageId)?.description}
                                        </p>
                                    )}
                                </div>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal} disabled={loading}>
                        Cancel
                    </Button>
                    {availableStages.length > 0 && (
                        <Button
                            variant="success"
                            onClick={handleStageChange}
                            disabled={!selectedStageId || loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Changing Stage...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Change Stage
                                </>
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdvanceStageButton;